import React, { useState } from 'react';
import {
    Modal, Popconfirm, Table, Checkbox, Input, Select, DatePicker, TimePicker, Form, Button, Tabs, ConfigProvider, Dropdown
} from 'antd';
import {
    ShoppingBag, RotateCcw, XCircle, AlertCircle, Wrench, Search, Paperclip, Smile, Send, ChevronDown,
    Minus, Plus, LogOut, SlidersHorizontal, Filter, RefreshCw, ArrowLeft,
    CheckCircle, Link as LinkIcon, FileSignature, FileText, QrCode, FileDown, Settings, Trash2, Pencil
} from 'lucide-react';
import Layout from '../../Layout';
import { useNavigate } from 'react-router-dom';

const { TextArea } = Input;

// ==========================================
// 1. DỮ LIỆU MẪU (MOCK DATA)
// ==========================================
const historyData = [
    {
        key: '1', date: '12/08/2026', action: 'Cấp phát', code: 'BBBG_113', qty: 2,
        revokeQty: '--', related: '--', executor: 'Q', assetId: 'TS.245',
        handover: 'Cá nhân', representative: 'Lê Quang Hưng', dept: 'Chuyên viên kinh doanh'
    },
];

const historyColumns = [
    { title: 'MÃ', dataIndex: 'code', key: 'code' },
    { title: 'THỜI GIAN', dataIndex: 'date', key: 'date' },
    { title: 'SỐ LƯỢNG', dataIndex: 'qty', key: 'qty' },
    { title: 'NGƯỜI THỰC HIỆN', dataIndex: 'representative', key: 'representative' },
];

const assetTableData = [
    { key: '1', maTS: 'TS.245', taiSan: 'Tivi [NTHQ]', viTri: '--', soLuong: 2, daThuHoi: 2, datCoc: '--', nguoiNhan: 'Lê Quang Hưng', phongBan: 'Chuyên viên kinh doanh', trangThai: 'Đang sử dụng', xacNhan: 'Không' }
];

const assetTableColumns = [
    { title: 'Mã TS', dataIndex: 'maTS', key: 'maTS' },
    { title: 'Tài sản', dataIndex: 'taiSan', key: 'taiSan' },
    { title: 'Vị trí tài sản', dataIndex: 'viTri', key: 'viTri' },
    { title: 'Số lượng', dataIndex: 'soLuong', key: 'soLuong' },
    { title: 'Đã thu hồi', dataIndex: 'daThuHoi', key: 'daThuHoi' },
    { title: 'Đặt cọc', dataIndex: 'datCoc', key: 'datCoc' },
    { title: 'Tên người nhận', dataIndex: 'nguoiNhan', key: 'nguoiNhan' },
    { title: 'Phòng ban', dataIndex: 'phongBan', key: 'phongBan' },
    { title: 'Trạng thái', dataIndex: 'trạngThai', key: 'trạngThai' },
    { title: 'Xác nhận', dataIndex: 'xacNhan', key: 'xacNhan' },
];

// ==========================================
// 2. COMPONENT CON HIỂN THỊ
// ==========================================
const InfoItem = ({ label, value }) => (
    <div className="mb-4">
        <p className="text-gray-400 dark:text-gray-500 text-[12px] mb-1">{label}</p>
        <p className="text-gray-800 dark:text-gray-200 text-[13px] font-medium">{value}</p>
    </div>
);

const CollapsibleSection = ({ title, extra, children, defaultExpanded = true, noPadding = false }) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    return (
        <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300">
            <div
                className="flex justify-between items-center px-4 sm:px-5 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <h3 className="text-[15px] font-bold text-gray-800 dark:text-gray-200">{title}</h3>
                <div className="flex items-center space-x-3 text-gray-400">
                    {extra && <div onClick={e => e.stopPropagation()} className="cursor-pointer hover:text-gray-600 transition-colors">{extra}</div>}
                    <button className="outline-none text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                        {isExpanded ? <Minus size={18} strokeWidth={1.5} /> : <Plus size={18} strokeWidth={1.5} />}
                    </button>
                </div>
            </div>
            {isExpanded && (
                <div className={`border-t border-gray-100 dark:border-gray-700 animate-fade-in ${noPadding ? '' : 'p-4 sm:p-5'}`}>
                    {children}
                </div>
            )}
        </div>
    );
};

// ==========================================
// 3. COMPONENT CHÍNH
// ==========================================
const AssetDetail = () => {
    const navigate = useNavigate();
    const [confirmIssueModal, setConfirmIssueModal] = useState(false);

    // State quản lý danh sách tin nhắn thảo luận và ô input
    const [discussions, setDiscussions] = useState([
        { id: 1, author: 'Lê Việt Thắng', time: '15:37 10/07/2026', text: 'Agent test 2026-07-10' }
    ]);
    const [inputMessage, setInputMessage] = useState('');

    const handleSendMessage = () => {
        if (!inputMessage.trim()) return;
        const newMessage = {
            id: Date.now(),
            author: 'Admin Hệ Thống',
            time: 'Vừa xong',
            text: inputMessage.trim()
        };
        setDiscussions([...discussions, newMessage]);
        setInputMessage('');
    };

    const rightClickMenuItems = [
        { key: 'approve', icon: <CheckCircle size={16} className="text-gray-500" />, label: 'Duyệt điều chuyển' },
        { 
            key: 'sign', 
            icon: <FileSignature size={16} className="text-gray-500" />, 
            label: 'Tạo hồ sơ ký số',
            onClick: () => navigate('/submit-for-signature/add')
        },
        { type: 'divider' },
        { 
            key: 'confirm_issue', 
            icon: <CheckCircle size={16} className="text-gray-500" />, 
            label: 'Xác nhận cấp phát tài sản',
            onClick: () => setConfirmIssueModal(true)
        },
        { key: 'link_object', icon: <LinkIcon size={16} className="text-gray-500" />, label: 'Liên kết đối tượng liên quan' },
        { type: 'divider' },
        { 
            key: 'form', 
            icon: <FileText size={16} className="text-gray-500" />, 
            label: 'Biểu mẫu',
            children: [
                { 
                    key: 'qrcode', 
                    label: 'Qrcode',
                    icon: <QrCode size={16} className="text-gray-500" />,
                    children: [
                        { key: 'export', label: 'Xuất file', icon: <FileDown size={16} className="text-gray-500" /> }
                    ]
                }
            ]
        },
        { 
            key: 'edit', 
            icon: <Pencil size={16} className="text-gray-500" />, 
            label: 'Sửa',
            onClick: () => navigate('/assets/edit')
        },
        { key: 'delete', icon: <Trash2 size={16} className="text-gray-500" />, label: 'Xóa' },
        { type: 'divider' },
        { key: 'customize', icon: <Settings size={16} className="text-gray-500" />, label: 'Tùy chỉnh' },
    ];

    return (
        <Layout>
            {/* TOOLBAR */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 sm:px-4 py-2.5 flex flex-col sm:flex-row justify-between items-start sm:items-center sticky top-[64px] z-10 shadow-sm gap-2 sm:gap-0">
                <div className="flex items-center">
                    <a href="/assets/allocation" className="w-8 h-8 mr-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors">
                        <ArrowLeft size={16} />
                    </a>
                    <h2 className="font-bold text-gray-800 dark:text-gray-100 text-[15px]">Chi tiết cấp phát</h2>
                </div>

                <div className="flex items-center flex-wrap gap-x-2 sm:gap-x-4 gap-y-2 text-sm w-full sm:w-auto sm:justify-end justify-start">
                    <button onClick={() => setConfirmIssueModal(true)} className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                        <CheckCircle size={15} className="mr-1.5 text-gray-400" /> Xác nhận
                    </button>
                    <button className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                        <LinkIcon size={15} className="mr-1.5 text-gray-400" /> Liên kết
                    </button>
                    <button onClick={() => navigate('/submit-for-signature/add')} className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                        <FileSignature size={15} className="mr-1.5 text-gray-400" /> Ký số
                    </button>
                    <Dropdown menu={{ items: rightClickMenuItems }} trigger={['click']}>
                        <button className="flex items-center text-gray-600 hover:text-[#3e91ab] font-medium transition-colors">
                            <FileText size={15} className="mr-1.5 text-gray-400" /> Biểu mẫu <ChevronDown size={14} className="ml-1" />
                        </button>
                    </Dropdown>
                </div>
            </div>

            {/* NỘI DUNG CHÍNH 2 CỘT */}
            <div className="mx-auto py-4 grid grid-cols-1 lg:grid-cols-3 gap-5 items-start mt-1">

                {/* CỘT TRÁI */}
                <div className="lg:col-span-2 flex flex-col gap-5">
                    <CollapsibleSection title="Lịch sử thu hồi" noPadding>
                        <div className="overflow-x-auto">
                            <Table columns={historyColumns} dataSource={historyData} pagination={false} size="middle" scroll={{ x: '100%' }} />
                        </div>
                    </CollapsibleSection>

                    <CollapsibleSection title="Thông tin chung">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-2">
                            <InfoItem label="Mã CP" value="BBBG_113" />
                            <InfoItem label="Tên cấp phát" value="TEST - NTHQ" />
                            <div>
                                <p className="text-gray-400 text-[12px] mb-1">Người cấp phát</p>
                                <div className="inline-flex items-center bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-full text-xs font-semibold">
                                    <span className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center mr-1.5 text-[10px]">N</span>
                                    Quintessa Nguyễn
                                </div>
                            </div>
                            <InfoItem label="Phòng ban" value="Chuyên viên kinh doanh" />
                            <InfoItem label="Cấp phát cho" value="Cá nhân" />
                            <InfoItem label="Ngày cấp" value="20/05/2026" />
                            <InfoItem label="Thời gian cấp phát" value="20:00" />
                            <InfoItem label="Địa điểm bàn giao" value="--" />
                            <InfoItem label="Ngày tạo" value="20/05/2026" />
                            <InfoItem label="Dự kiến ngày trả" value="--" />
                            <InfoItem label="Lý do cấp phát" value="--" />
                        </div>
                    </CollapsibleSection>

                    <CollapsibleSection title="Danh sách tài sản" noPadding>
                        <div className="overflow-x-auto">
                            <Table columns={assetTableColumns} dataSource={assetTableData} pagination={false} size="middle" scroll={{ x: 'max-content' }} />
                        </div>
                    </CollapsibleSection>
                </div>

                {/* CỘT PHẢI */}
                <div className="lg:col-span-1 flex flex-col gap-5">
                    <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700">
                        <Tabs
                            defaultActiveKey="1"
                            className="px-4 pt-2 custom-antd-tabs"
                            items={[
                                {
                                    key: '1', 
                                    label: 'Thảo luận',
                                    children: (
                                        <div className="py-4 flex flex-col max-h-[420px]">
                                            {/* Danh sách tin nhắn hiển thị chuẩn theo hình mẫu */}
                                            <div className="flex-1 overflow-y-auto space-y-4 pr-1 mb-3">
                                                {discussions.map((item) => (
                                                    <div key={item.id} className="flex items-start">
                                                        <div className="w-8 h-8 mr-3 rounded-full bg-[#84cc16] text-white flex items-center justify-center text-xs font-bold shrink-0">
                                                            {item.author.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-bold text-gray-800 dark:text-gray-200 mb-1">{item.author}</p>
                                                            <div className="bg-gray-100 dark:bg-gray-700/60 px-3 py-2 rounded-xl text-xs text-gray-800 dark:text-gray-200 inline-block shadow-sm">
                                                                {item.text}
                                                            </div>
                                                            <p className="text-[10px] text-gray-400 mt-1">{item.time}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Ô input nhập chat phía dưới */}
                                            <div className="relative mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
                                                <Input 
                                                    placeholder="Viết thảo luận..." 
                                                    value={inputMessage}
                                                    onChange={(e) => setInputMessage(e.target.value)}
                                                    onPressEnter={handleSendMessage}
                                                    className="rounded-full pl-4 pr-20 py-2 bg-gray-50 border-gray-200 text-xs" 
                                                />
                                                <div className="absolute right-3 top-4 flex space-x-2 text-gray-400">
                                                    <Paperclip size={14} className="cursor-pointer hover:text-gray-600" />
                                                    <Smile size={14} className="cursor-pointer hover:text-gray-600" />
                                                    <Send size={14} className="cursor-pointer hover:text-[#3e91ab]" onClick={handleSendMessage} />
                                                </div>
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    key: '2',
                                    label: 'Lịch sử hoạt động',
                                    children: (
                                        <div className="pb-4 px-2">
                                            <div className="relative pl-6 space-y-4 max-h-[420px] overflow-y-auto pr-1">
                                                <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                                                <div className="relative flex items-start">
                                                    <div className="absolute -left-6 top-1 w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-sm z-10">
                                                        <ShoppingBag size={10} />
                                                    </div>
                                                    <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md border border-gray-100 dark:border-gray-700 ml-2">
                                                        <p className="text-[12px] text-gray-800 dark:text-gray-200">
                                                            <span className="font-bold">Quintessa Nguyễn</span> đã liên kết đối tượng
                                                        </p>
                                                        <p className="text-[10px] text-gray-400 mt-0.5">🕒 16:41 ng 20/05/2026</p>
                                                        <p className="text-[11px] text-gray-500 mt-1">+ Quy trình: ĐỀ XUẤT MUA SẮM NỘI BỘ - NTHQ</p>
                                                    </div>
                                                    <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0 ml-2">N</div>
                                                </div>

                                                <div className="relative flex items-start">
                                                    <div className="absolute -left-6 top-1 w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center shadow-sm z-10">
                                                        <Plus size={10} />
                                                    </div>
                                                    <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md border border-gray-100 dark:border-gray-700 ml-2">
                                                        <p className="text-[12px] text-gray-800 dark:text-gray-200">
                                                            <span className="font-bold">Quintessa Nguyễn</span> đã tạo mới
                                                        </p>
                                                        <p className="text-[10px] text-gray-400 mt-0.5">🕒 16:40 ng 20/05/2026</p>
                                                        <p className="text-[11px] text-gray-500 mt-1">ID: 502 - BBBG_113</p>
                                                    </div>
                                                    <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0 ml-2">N</div>
                                                </div>

                                            </div>
                                        </div>
                                    )
                                }
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* MODAL XÁC NHẬN CẤP PHÁT TÀI SẢN */}
            <Modal
                title={<span className="font-semibold text-gray-800 text-sm">Xác nhận cấp phát tài sản</span>}
                open={confirmIssueModal}
                onCancel={() => setConfirmIssueModal(false)}
                footer={[
                    <Button key="cancel" onClick={() => setConfirmIssueModal(false)}>
                        HỦY BỎ
                    </Button>,
                    <Button key="submit" type="primary" className="bg-[#3e91ab] hover:bg-[#3e91ab] border-none">
                        CẬP NHẬT
                    </Button>,
                ]}
                width={550}
            >
                <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
                    <Table
                        size="small"
                        pagination={false}
                        rowSelection={{ type: 'checkbox' }}
                        columns={[
                            { title: 'Mã TS', dataIndex: 'maTS' },
                            { title: 'Tài sản', dataIndex: 'taiSan' },
                            { title: 'Số lượng', dataIndex: 'soLuong' },
                            { title: 'Tên người nhận', dataIndex: 'nguoiNhan' },
                        ]}
                        dataSource={[
                            { key: '1', maTS: 'TS.245', taiSan: 'Tivi [NTHQ]', soLuong: 2, nguoiNhan: 'Lê Quang Hưng' }
                        ]}
                    />
                </div>
            </Modal>

            {/* CSS TÙY CHỈNH */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-antd-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn { color: #3e91ab !important; font-weight: 600; }
                .custom-antd-tabs .ant-tabs-ink-bar { background: #3e91ab !important; }
                .custom-antd-tabs .ant-tabs-tab:hover { color: #3e91ab !important; }
                
                .ant-dropdown .ant-dropdown-menu-item, 
                .ant-dropdown-menu-submenu-title {
                    padding-top: 8px !important;
                    padding-bottom: 8px !important;
                    display: flex !important;
                    align-items: center !important;
                }
                .ant-dropdown .ant-dropdown-menu-item .lucide,
                .ant-dropdown .ant-dropdown-menu-submenu-title .lucide {
                    margin-right: 8px !important;
                }
            `}} />
        </Layout>
    );
};

export default AssetDetail;